/**
 * RentalReturnAndCancellationSection — "ใบแจ้งยกเลิก / ใบตามเก็บอุปกรณ์" ในหน้ารายละเอียดการเช่า (28 ต.ค. 2026)
 *
 * ทางเข้าหน้าเต็มของสองโมดูลจากสัญญา (มติ PO: ดูทั้งเรคคอร์ดเป็นหน้า มี URL ของตัวเอง) — เดิมจากหน้าสัญญาไปได้แค่
 * หน้ารายการ (และ ?rentalId= ที่เปิดฟอร์ม "สร้าง") ไม่มีทางเปิดใบที่มีอยู่แล้วของสัญญานี้ตรง ๆ
 *
 * - ใบแจ้งยกเลิก: GET /rentals/cancellation-notices/rental/{id} · perm rentals.view (หน้านี้ต้องมีอยู่แล้ว)
 * - ใบตามเก็บ: GET /equipment-returns/items?rental_id= · perm equipment_returns.view — ไม่มีสิทธิ์ = ไม่ยิง API ไม่แสดงกลุ่มนี้
 * - ไม่มีทั้งสองอย่าง = ไม่มีการ์ดเปล่า
 */
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiCloseCircleLine, RiArrowGoBackLine, RiArrowRightSLine } from 'react-icons/ri';
import rentalCancellationService, { RentalCancellationNotice } from '../../services/rentalCancellationService';
import equipmentReturnItemService, { ReturnItem, OPEN_RETURN_ITEM_STATUSES } from '../../services/equipmentReturnItemService';
import { usePermission } from '../../hooks/usePermission';
import { formatDate } from '../../utils/dateUtils';
import { returnItemShortLabel } from '../../utils/returnItemLabel';
import { cancellationNoticeUrl, returnRequestUrl } from '../../utils/returnCancellationLinks';
import { detailNavState } from '../detail/detailNavigation';

// FB-2026-0010: ค่าคงที่กลาง (รวม "อยู่ที่บริษัท รปภ" — ยังเปิด)
const OPEN_RETURN = new Set<string>(OPEN_RETURN_ITEM_STATUSES);

const RentalReturnAndCancellationSection: React.FC<{ rentalId: number }> = ({ rentalId }) => {
  const { hasPermission } = usePermission();
  const location = useLocation();
  const hereState = detailNavState(location);
  const canNotices = hasPermission('rentals.view');
  const canReturns = hasPermission('equipment_returns.view');

  const { data: notices } = useQuery({
    queryKey: ['cancellation-notices', 'by-rental', rentalId],
    queryFn: () => rentalCancellationService.getRentalCancellationHistory(rentalId) as Promise<RentalCancellationNotice[]>,
    enabled: canNotices && !!rentalId,
    retry: false,
    staleTime: 30 * 1000,
  });
  const { data: returns } = useQuery({
    queryKey: ['equipment-return-items', 'by-rental', rentalId],
    queryFn: () => equipmentReturnItemService.getReturnItems({ rental_id: rentalId, limit: 100 }),
    enabled: canReturns && !!rentalId,
    retry: false,
    staleTime: 30 * 1000,
  });

  const groups = useMemo(() => {
    const m = new Map<number, ReturnItem[]>();
    for (const it of returns?.items ?? []) {
      m.set(it.return_request_id, [...(m.get(it.return_request_id) ?? []), it]);
    }
    return Array.from(m.entries()).sort((a, b) => b[0] - a[0]);
  }, [returns]);

  const noticeList = [...(notices ?? [])].sort((a, b) => b.id - a.id);
  if (noticeList.length === 0 && groups.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200" data-testid="rental-return-cancellation-section">
      <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
        <h2 className="text-lg font-medium text-secondary-900">ใบแจ้งยกเลิก / ใบตามเก็บอุปกรณ์</h2>
      </div>
      <div className="divide-y divide-secondary-100">
        {noticeList.map((n) => (
          <Link key={`n-${n.id}`} to={cancellationNoticeUrl(n.id)} state={hereState}
            className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 hover:bg-secondary-50"
            data-testid={`rental-notice-link-${n.id}`}>
            <div className="min-w-0 flex items-start gap-2">
              <RiCloseCircleLine className="w-4 h-4 mt-0.5 text-danger-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-secondary-900">ใบแจ้งยกเลิก #{n.id} · {rentalCancellationService.getCancellationStatusLabel(n.status)}</p>
                <p className="text-xs text-secondary-500">แจ้ง {formatDate(n.notice_date)} · จะยกเลิก {formatDate(n.planned_cancellation_date)}</p>
              </div>
            </div>
            <RiArrowRightSLine className="w-5 h-5 text-secondary-400 flex-shrink-0" />
          </Link>
        ))}
        {groups.map(([reqId, rows]) => {
          const open = rows.filter((r) => OPEN_RETURN.has(r.status)).length;
          return (
            <Link key={`r-${reqId}`} to={returnRequestUrl(reqId)} state={hereState}
              className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 hover:bg-secondary-50"
              data-testid={`rental-return-request-link-${reqId}`}>
              <div className="min-w-0 flex items-start gap-2">
                <RiArrowGoBackLine className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900">
                    ใบตามเก็บ #{reqId} · {rows.length} ชิ้น{open > 0 ? ` · ยังเปิด ${open}` : ' · ปิดครบแล้ว'}
                  </p>
                  <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">{rows.map((r) => returnItemShortLabel(r)).join(' · ')}</p>
                </div>
              </div>
              <RiArrowRightSLine className="w-5 h-5 text-secondary-400 flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default RentalReturnAndCancellationSection;
